import { useEffect, useState } from "react";
import CommonPageLayout from "Components/CommonPageLayout";
import axios from "axios";
import { getCookie } from "JS/cookie";
import "Styles/css/calculator.css";

const VAT = 0.1;
const INIT_DISCOUNT_RATE = 0;
const COMPOUND_TYPE_FEE = 70000;
const NOT_NOTIFIED_FEE = 6000;
const EXCEED_ITEM_COST = 2000;
const MARK_TYPE = {
  text: "문자/도형상표",
  compound: "복합상표",
};
const MARK_FEE_OBJECT = {
  general: {
    type: "general",
    label: "일반출원",
    agentFee: 50000,
    patentOfficeFee: 46000,
    hasCompound: false,
  },
  search: {
    type: "search",
    label: "조사출원",
    agentFee: 100000,
    patentOfficeFee: 46000,
    hasCompound: true,
  },
  fast: {
    type: "fast",
    label: "신속출원",
    agentFee: 200000,
    patentOfficeFee: 206000,
    hasCompound: false,
  },
  fastSearch: {
    type: "fastsearch",
    label: "신속조사출원",
    agentFee: 250000,
    patentOfficeFee: 206000,
    hasCompound: true,
  },
};

const initialCategory = {
  id: 0,
  productType: 1,
  productQuantity: 1,
  applicationType: MARK_FEE_OBJECT.general.type,
  isNotNotified: false,
  patentOfficeFee: MARK_FEE_OBJECT.general.patentOfficeFee,
  isExpanded: true,
};

export const calculateDiscountedPrice = (originalPrice, discountRate) => {
  const discountRateDecimal = discountRate / 100;
  const discountAmount = originalPrice * discountRateDecimal;
  return discountAmount;
};

export const formatNumberWithWon = (number, showWon = true) => {
  if (number === null || number === undefined || isNaN(number)) return "";

  const formattedNumber = number.toLocaleString("ko-KR");

  if (showWon) {
    return formattedNumber + "원";
  } else {
    return formattedNumber;
  }
};

const ApplicationCalculator = () => {
  const [selectedMarkType, setSelctedMarkType] = useState("text");
  const [productApplicationTypes, setProductApplicationTypes] = useState([
    initialCategory,
  ]);
  const [categories, setCategories] = useState([]);
  const [discountRate, setDiscountRate] = useState(INIT_DISCOUNT_RATE);

  const totalSummary = productApplicationTypes.reduce(
    (accumulator, currentItem) => {
      accumulator.productType = accumulator.productType || [];
      accumulator.productType.push(currentItem.productType);

      accumulator.productQuantity = accumulator.productQuantity || [];
      accumulator.productQuantity.push(currentItem.productQuantity);

      accumulator.applicationType = accumulator.applicationType || [];
      accumulator.applicationType.push(currentItem.applicationType);

      accumulator.isNotNotified = accumulator.isNotNotified || [];
      accumulator.isNotNotified.push(currentItem.isNotNotified);

      accumulator.patentOfficeFee = accumulator.patentOfficeFee || [];
      accumulator.patentOfficeFee.push(currentItem.patentOfficeFee);

      return accumulator;
    },
    {}
  );

  const handleMarkTypeChange = (event) => {
    setSelctedMarkType(event.target.value);
  };

  const handleDiscountRateChange = (event) => {
    setDiscountRate(event.target.value);
  };

  const handleIsNotifiedChange = (event) => {
    const splitedArr = event.target.name.split("-");
    const targetId = Number(splitedArr[splitedArr.length - 1]);
    const updatedProduct = productApplicationTypes.map((product) => {
      if (product.id === targetId) {
        return { ...product, isNotNotified: !product.isNotNotified };
      }
      return product;
    });

    setProductApplicationTypes(updatedProduct);
  };

  const handleQuantityChange = (event) => {
    const splitedArr = event.target.name.split("-");
    const targetId = Number(splitedArr[splitedArr.length - 1]);
    const productQuantity = Number(event.target.value);
    const updatedProduct = productApplicationTypes.map((product) => {
      if (product.id === targetId) {
        return { ...product, productQuantity };
      }
      return product;
    });

    setProductApplicationTypes(updatedProduct);
  };

  const handleApplicationTypeChange = (event) => {
    const splitedArr = event.target.name.split("-");
    const targetId = Number(splitedArr[splitedArr.length - 1]);
    const applicationType = event.target.value;

    const updatedProduct = productApplicationTypes.map((product) => {
      if (product.id === targetId) {
        return {
          ...product,
          applicationType,
          patentOfficeFee: MARK_FEE_OBJECT[applicationType].patentOfficeFee,
        };
      }
      return product;
    });

    setProductApplicationTypes(updatedProduct);
  };

  const handleExpandedChange = (targetId) => {
    const updatedProduct = productApplicationTypes.map((product) => {
      if (product.id === targetId) {
        return {
          ...product,
          isExpanded: !product.isExpanded,
        };
      }
      return product;
    });
    setProductApplicationTypes(updatedProduct);
  };

  const addProduct = () => {
    const lastIndex =
      productApplicationTypes[productApplicationTypes.length - 1].id;
    setProductApplicationTypes((prev) => [
      ...prev,
      { ...initialCategory, id: lastIndex + 1 },
    ]);
  };

  const deleteProduct = (id) => {
    const filteredArray = productApplicationTypes.filter(
      (product) => product.id !== id
    );
    setProductApplicationTypes([...filteredArray]);
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get("/v1/agency/get-product-kind", {
        headers: {
          "Content-Type": "application/json",
          "access-token": getCookie("pickAdminMyToken"),
        },
      });
      setCategories(
        response.data.map((item) => ({
          code: item.kind_code,
          name: item.kind_name,
        }))
      );
    };

    fetchData();
  }, []);

  return (
    <CommonPageLayout title="APPLICATION CALCULATOR">
      <div className="calculator-container">
        <div>
          <section className="section-container">
            <Header title="상표유형" HeadingTag="h3" />
            <div className="radio-list">
              {Object.keys(MARK_TYPE).map((key, idx) => (
                <RadioItem
                  key={key + idx}
                  type={key}
                  label={MARK_TYPE[key]}
                  value={key}
                  checked={selectedMarkType === key}
                  onChange={handleMarkTypeChange}
                  name="mark-type"
                />
              ))}
            </div>
          </section>
          <section className="section-container">
            <Header title="상품류 및 출원유형" HeadingTag="h3" />
            {productApplicationTypes.map((product, idx) => (
              <ProductCategory
                key={product.id}
                productIndex={idx}
                categories={categories}
                isCompound={selectedMarkType === "compound"}
                isNotNotified={product.isNotNotified}
                onCheckboxChange={handleIsNotifiedChange}
                onApplicationTypeChange={handleApplicationTypeChange}
                onQuantityChange={handleQuantityChange}
                onButtonClick={deleteProduct}
                onExpandedChange={handleExpandedChange}
                {...product}
              />
            ))}
          </section>
          <div className="btn-container">
            <button onClick={addProduct}>+ 상품류 추가</button>
          </div>
          <section className="section-container">
            <Header
              title="할인율 적용 (%)"
              HeadingTag="h3"
              SubComponent={() => (
                <select
                  name="discountRate"
                  id="discountRate"
                  value={discountRate}
                  onChange={handleDiscountRateChange}
                >
                  {Array.from({ length: 10 + 1 }, (_, index) => {
                    const value = index * 5;
                    const defaultValue = value === 0;
                    return (
                      <option
                        key={value}
                        value={value}
                        defaultValue={defaultValue}
                      >
                        {value}
                      </option>
                    );
                  })}
                </select>
              )}
            />
          </section>
        </div>
        <div className="summary">
          <Summary
            markType={MARK_TYPE[selectedMarkType]}
            discountRate={discountRate}
            totalSummary={totalSummary}
          />
        </div>
      </div>
    </CommonPageLayout>
  );
};

export default ApplicationCalculator;

const ProductCategory = ({
  categories,
  id,
  productIndex,
  productQuantity,
  applicationType,
  patentOfficeFee,
  isNotNotified,
  isCompound,
  isExpanded,
  onCheckboxChange,
  onApplicationTypeChange,
  onQuantityChange,
  onButtonClick,
  onExpandedChange,
}) => {
  // TODO: includes -> MARK_FEE_OBJECT에서 hasCompound 사용
  const isAdditionalTypeFee =
    applicationType.toLowerCase().includes("search") && isCompound;
  const addNotNotifedFee = isNotNotified ? NOT_NOTIFIED_FEE : 0;
  const addPatentOfficeFee = isAdditionalTypeFee ? COMPOUND_TYPE_FEE : 0;
  const addExceedItemCost =
    productQuantity > 10 ? (productQuantity - 10) * EXCEED_ITEM_COST : 0;
  const totalPatentOfficeFee =
    patentOfficeFee + addNotNotifedFee + addExceedItemCost;
  const totalPatentOfficeFeeString = formatNumberWithWon(totalPatentOfficeFee);
  const total =
    MARK_FEE_OBJECT[applicationType].agentFee +
    totalPatentOfficeFee +
    addPatentOfficeFee;
  const totalString = formatNumberWithWon(total);

  return (
    <div className="classification">
      <Header
        title={`상품분류 #${productIndex + 1}`}
        HeadingTag="h4"
        subContent={totalString}
      />
      <div className={isExpanded ? "" : "collapse-container"}>
        <div className="group">
          <div>
            <div className="group-item">
              <label htmlFor="category">상품류</label>
              <select name="category" id="category">
                {categories.map((category) => (
                  <option value={category.code} key={category.code}>
                    {category.code}류 - {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="group-item">
              <label htmlFor="quantity">지정상품 수량</label>
              <input
                type="number"
                id="quantity"
                min={0}
                max={50}
                name={`product-quantity-${id}`}
                value={productQuantity}
                onChange={onQuantityChange}
              />
            </div>
          </div>
          <p className="tooltip">
            * 수량 10개 초과 시 개당 2,000원 추가 요금 발생
          </p>
          <div className="remark-checkbox">
            <input
              type="checkbox"
              id={`remark-checkbox-${id}`}
              name={`remark-checkbox-${id}`}
              checked={isNotNotified}
              onChange={onCheckboxChange}
            />
            <label htmlFor={`remark-checkbox-${id}`}>비고시 상품</label>
          </div>
        </div>
        <div className="group">
          <Header
            title="출원유형"
            HeadingTag="h5"
            subContent={formatNumberWithWon(
              isAdditionalTypeFee
                ? MARK_FEE_OBJECT[applicationType].agentFee + COMPOUND_TYPE_FEE
                : MARK_FEE_OBJECT[applicationType].agentFee
            )}
          />
          <div className="radio-list application-list">
            {Object.keys(MARK_FEE_OBJECT).map((key, idx) => {
              const isAdditionalTypeFee =
                MARK_FEE_OBJECT[key].type.includes("search") && isCompound;
              const label = isAdditionalTypeFee
                ? `${MARK_FEE_OBJECT[key].label} (+${formatNumberWithWon(
                    MARK_FEE_OBJECT[key].agentFee + COMPOUND_TYPE_FEE,
                    false
                  )})`
                : `${MARK_FEE_OBJECT[key].label} (+${formatNumberWithWon(
                    MARK_FEE_OBJECT[key].agentFee,
                    false
                  )})`;

              return (
                <RadioItem
                  key={key + idx}
                  type={`${MARK_FEE_OBJECT[key].type}-${id}`}
                  label={label}
                  value={key}
                  checked={applicationType === key}
                  onChange={onApplicationTypeChange}
                  name={`application-type-${id}`}
                />
              );
            })}
          </div>
        </div>
        <div className="group">
          <Header
            title="특허청 관납료"
            HeadingTag="h5"
            subContent={totalPatentOfficeFeeString}
          />
        </div>
      </div>
      <div className="group">
        <div className="btn-container product-button">
          <button onClick={() => onExpandedChange(id)}>
            {isExpanded ? "접기 ▲" : "펼치기 ▼"}
          </button>
          {id !== 0 && (
            <button className="warning" onClick={() => onButtonClick(id)}>
              삭제
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const RadioItem = ({ type, label, name, value, checked, onChange }) => {
  return (
    <div className="type-item">
      <input
        type="radio"
        id={type}
        value={value}
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={type}>{label}</label>
    </div>
  );
};

const Summary = ({ markType, discountRate, totalSummary }) => {
  // TODO: 수정 필요
  const isCompound = markType === "복합상표";
  const { productQuantity, applicationType, isNotNotified, patentOfficeFee } =
    totalSummary;

  const exceedItemCount = productQuantity.reduce((acc, cur) => {
    // TODO: magic number
    const itemExcess = Math.max(0, cur - 10);
    return acc + itemExcess;
  }, 0);
  const notNotifiedCount = isNotNotified.reduce((acc, cur) => {
    return cur === true ? acc + 1 : acc;
  }, 0);
  const sumPatentOfficeFee = patentOfficeFee.reduce((acc, cur) => {
    return acc + cur;
  }, 0);

  const applicationTypeCount = applicationType.reduce((acc, cur) => {
    const existingItem = acc.find((item) => Object.keys(item)[0] === cur);

    if (existingItem) {
      existingItem[cur]++;
    } else {
      acc.push({ [cur]: 1 });
    }
    return acc;
  }, []);
  const sumApplicationType = applicationType.reduce((acc, cur) => {
    const cost = MARK_FEE_OBJECT[cur].agentFee;

    if (!!isCompound && cur.toLowerCase().includes("search")) {
      return acc + cost + COMPOUND_TYPE_FEE;
    }
    return acc + cost;
  }, 0);
  const totalVAT = sumApplicationType * VAT;

  const totalPatentOfficeFee =
    sumPatentOfficeFee +
    notNotifiedCount * NOT_NOTIFIED_FEE +
    exceedItemCount * EXCEED_ITEM_COST;

  const discountedCost = calculateDiscountedPrice(
    sumApplicationType + totalVAT,
    discountRate
  );

  const totalCost =
    sumApplicationType + totalPatentOfficeFee + totalVAT - discountedCost;

  return (
    <section className="section-container">
      <Header title="전체 요약" HeadingTag="h3" />
      <div className="price-list">
        <div className="list-header group">
          <SummaryGroupItem label="이름" value="합계" />
        </div>
        <div className="group">
          <SummaryGroupItem label="상표유형" value={markType} />
        </div>
        <div className="group">
          <SummaryGroupItem
            label="마크픽 수수료"
            value={formatNumberWithWon(sumApplicationType)}
          />
          <div className="group-item-unit">
            {applicationTypeCount.map((type) => {
              const property = Object.keys(type)[0];
              const values = Object.values(type)[0];
              const markType = MARK_FEE_OBJECT[property];
              const addCompoundCost =
                isCompound && property.toLowerCase().includes("search")
                  ? COMPOUND_TYPE_FEE * values
                  : 0;
              const label = `${markType.label} x${values}`;
              const value = formatNumberWithWon(
                MARK_FEE_OBJECT[Object.keys(type)].agentFee * values +
                  addCompoundCost
              );
              return (
                <SummaryGroupItem key={label} label={label} value={value} />
              );
            })}
          </div>
        </div>
        <div className="group">
          <SummaryGroupItem
            label="특허청 관납료"
            value={formatNumberWithWon(totalPatentOfficeFee)}
          />
          <div className="group-item-unit">
            {applicationTypeCount.map((type) => {
              const property = Object.keys(type)[0];
              const values = Object.values(type)[0];
              const markType = MARK_FEE_OBJECT[property];
              const label = `${markType.label} x${values}`;
              const value = formatNumberWithWon(
                markType.patentOfficeFee * values
              );

              return (
                <SummaryGroupItem key={label} label={label} value={value} />
              );
            })}

            {!!exceedItemCount && (
              <SummaryGroupItem
                label={`상품군 초과 x ${exceedItemCount}`}
                value={formatNumberWithWon(exceedItemCount * EXCEED_ITEM_COST)}
              />
            )}
            {!!notNotifiedCount && (
              <SummaryGroupItem
                label={`비고시 x${notNotifiedCount}`}
                value={formatNumberWithWon(notNotifiedCount * NOT_NOTIFIED_FEE)}
              />
            )}
          </div>
        </div>
        <div className="group">
          <SummaryGroupItem label="VAT" value={formatNumberWithWon(totalVAT)} />
        </div>
        <div className="group">
          <SummaryGroupItem
            label={`할인율 ${discountRate}%`}
            value={`- ${formatNumberWithWon(discountedCost)}`}
          />
        </div>
        <div className="group">
          <SummaryGroupItem
            label="합계"
            value={formatNumberWithWon(totalCost)}
          />
        </div>
      </div>
    </section>
  );
};

const SummaryGroupItem = ({ label, value }) => {
  return (
    <div className="group-item">
      <p className="group-item-label">{label}</p>
      <p className="group-item-value">{value}</p>
    </div>
  );
};

const Header = ({ title, HeadingTag, subContent, SubComponent }) => {
  return (
    <div className="header">
      <HeadingTag>{title}</HeadingTag>
      {subContent && <p>{subContent}</p>}
      {SubComponent && <SubComponent />}
    </div>
  );
};
